import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { checkLoginController, loginController } from "./auth.controller";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import {
	Form,
	FormField,
	FormItem,
	FormLabel,
	FormControl,
	FormMessage,
	FormDescription,
} from "@/components/ui/form";
import { toaster } from "@/components/Toaster";
import { Link, useNavigate } from "react-router-dom";
import { dashboardPath } from "@/app/routes/route";
import useGcpsUIController from "@/hooks/useGlobalContext";
import { useEffect, useState } from "react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { FieldDescription } from "@/components/ui/field";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { cn, handleMutationError, withPrefix } from "@/lib/utils";
import { useTheme } from "@/hooks/useTheme";
import { routes } from "@/app/routes/paths";

const loginSchema = z.object({
	username: z.string().min(1, "Resident ID is required"),
	password: z.string().min(6, "Password must be at least 6 characters")
});

const checkLoginSchema = z.object({
	username: z.string().min(1, "Resident ID is required"),
	password: z.string().min(6, "Password must be at least 6 characters"),
	sms_code: z.string().min(6, "SMS code must be at least 6 characters"),
});

type FormValues = z.infer<typeof loginSchema>;
type CheckFormValues = z.infer<typeof checkLoginSchema>;

export const LoginPage = () => {
	const { setLoggedInUser, controller } = useGcpsUIController();
	const { pageTitle } = controller;
	const [step, setStep] = useState<"login" | "2fa">("login");
	const [otpMessage, setOtpMessage] = useState<string | null>(null);
	const [credentials, setCredentials] = useState({ username: "", password: "" });
	const { theme } = useTheme();

	const getColorType = () => {
		if (theme === "system") {
			return window.matchMedia("(prefers-color-scheme: dark)").matches
				? "dark"
				: "light";
		}
		return theme;
	};

	const [colorType, setColorType] = useState(getColorType);

	const navigate = useNavigate();

	const form = useForm<FormValues>({
		resolver: zodResolver(loginSchema),
		defaultValues: { username: "", password: "" },
	});

	const checkLoginForm = useForm<CheckFormValues>({
		resolver: zodResolver(checkLoginSchema),
		defaultValues: { username: form.getValues("username"), password: form.getValues("password"), sms_code: "" },
	});

	const mutation = useMutation({
		mutationFn: step === 'login' ? checkLoginController : loginController,
		onError: handleMutationError,
		onSuccess: (data) => {
			if (data.data.email1 || data.data.email2) {
				// Proceed to 2FA step
				checkLoginForm.setValue('username', credentials.username);
				checkLoginForm.setValue('password', credentials.password);
				setStep("2fa");
				setOtpMessage(data.message);
				toaster("success", "A verification code has been sent to your registered emails.");
				return;
			}

			setLoggedInUser(data.data.user);
			navigate(dashboardPath);
			toaster("success", "Signed in successfully");
		},
	});

	const { mutate } = mutation;
	const isPending = mutation.status === "pending";

	const onSubmit = (data: FormValues) => {
		setCredentials({ username: data.username, password: data.password });
		mutate({ username: data.username, password: data.password });
	};

	const onSubmitCheckForm = (data: CheckFormValues) => {
		mutate({ username: data.username, password: data.password, sms_code: data.sms_code });
	};

	useEffect(() => {
		setColorType(getColorType());
	}, [theme]);

	useEffect(() => {
		document.title = pageTitle ? `Login | ${pageTitle}` : "Resident Portal";
	}, [pageTitle]);

	return (
		<div className="min-h-screen grid grid-cols-1 md:grid-cols-2">
			<div className="hidden md:flex items-center justify-center p-8 bg-cover bg-right relative" style={{ backgroundImage: `url(${withPrefix("assets/images/login.jpg")})` }}>
				<div className={cn("absolute inset-0 bg-white/50", colorType === 'dark' && "bg-black/60")}></div>
				<div className="max-w-md text-center relative z-10">
					{/* <img src="" alt="Illustration" className="mx-auto object-cover" /> */}
					<h2 className={cn("mt-6 text-4xl font-semibold text-yellow-500", colorType === 'light' && "text-primary")}>Welcome back</h2>
					<p className={cn("text-primary mt-5 text-2xl", colorType === 'light' && "text-black")}>Access your resident portal to manage fees, documents and support.</p>
				</div>
			</div>

			<div className="flex items-center justify-center p-8">
				<div className="w-full max-w-lg bg-background border border-border rounded-2xl p-8 shadow-lg">
					<div className="flex items-center gap-4">
						<div className="rounded-md bg-primary/10 p-2">
							<img src={withPrefix("assets/images/favicon.png")} alt="logo" className="w-10 h-10" />
						</div>
						<div>
							<h1 className="text-2xl font-semibold">Resident Portal</h1>
							<p className="text-muted-foreground text-sm mt-1">Sign in to manage your account and requests.</p>
						</div>
					</div>
					{step === "2fa" && otpMessage && (
						<div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
							<p className="text-yellow-800" dangerouslySetInnerHTML={{ __html: otpMessage }}></p>
						</div>
					)}
					{step === "login" && (
						<Form {...form}>
							<form onSubmit={form.handleSubmit(onSubmit)} className="mt-6 space-y-4">
								<FormField
									control={form.control}
									name="username"
									render={({ field }) => (
										<FormItem>
											<FormControl>
												<Input label="Resident ID" placeholder="012235620" type="text" {...field} />
											</FormControl>
											<FormMessage />
											<FormDescription>Please enter resident number without the slash (e.g. PS/00/00/001/M should be PS0000001M)</FormDescription>
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="password"
									render={({ field }) => (
										<FormItem>
											<FormControl>
												<Input label="Password" placeholder="••••••••" type="password" {...field} />
											</FormControl>
											<FormMessage />
											<FormDescription>First time users should use this default password: zEY5APiC%</FormDescription>
										</FormItem>
									)}
								/>

								<div className="flex items-center justify-between">
									<Link className="text-sm text-primary hover:underline" to={routes.PASSWORD_RESET_PATH}>
										Forgot password?
									</Link>
								</div>

								<div className="flex items-center gap-2">
									<Button type="submit" disabled={isPending} className="flex-1">
										{isPending ? "Signing in..." : "Sign in"}
									</Button>
								</div>
							</form>
						</Form>

					)}
					{step === "2fa" && (
						<Form {...checkLoginForm}>
							<form onSubmit={checkLoginForm.handleSubmit(onSubmitCheckForm, console.log)} className="mt-6 space-y-4">
								<FormField
									control={checkLoginForm.control}
									name="sms_code"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Verification code</FormLabel>

											<FormControl>
												<InputOTP
													pattern={REGEXP_ONLY_DIGITS}
													maxLength={6}
													value={field.value}
													onChange={field.onChange}
												>
													<InputOTPGroup className="gap-2.5 *:data-[slot=input-otp-slot]:p-6 *:data-[slot=input-otp-slot]:text-[26px] *:data-[slot=input-otp-slot]:rounded-md *:data-[slot=input-otp-slot]:border w-full justify-evenly">
														<InputOTPSlot index={0} />
														<InputOTPSlot index={1} />
														<InputOTPSlot index={2} />
														<InputOTPSlot index={3} />
														<InputOTPSlot index={4} />
														<InputOTPSlot index={5} />
													</InputOTPGroup>
												</InputOTP>
											</FormControl>

											<FormDescription>
												Enter the 6-digit code sent to your email.
											</FormDescription>

											<FormMessage />
										</FormItem>
									)}
								/>
								<div className="flex flex-col gap-2">
									<Button type="submit" className="mb-5" disabled={mutation.isPending || !checkLoginForm.formState.isValid}>Verify</Button>
									<FieldDescription className="text-center">
										Not You? <a href="#" onClick={() => setStep('login')}>Change Account</a>
									</FieldDescription>
									<FieldDescription className="text-center">
										Didn&apos;t receive the code? <a href="#" onClick={form.handleSubmit(onSubmit)}>Resend</a>
									</FieldDescription>
								</div>
							</form>
						</Form>
					)}
				</div>
			</div>
		</div>
	);
};